/**
 * Core SubstrateEngine class for print simulation and substrate-specific color processing
 */

import { ColorEngine } from "../colorEngine";
import type { CMYK } from "../colorEngine";
import type {
  SubstrateProfile,
  PrintSimulationResult,
  PrintMetrics,
  ColorAdjustment,
  CMYKValues,
  PrintWarning,
  LayerStack,
  MultiLayerSimulationResult,
  LayerSimulationResult,
  PrintLayer,
  PrintLayerWithSpot,
  SpotColor,
  SpotColorConversionResult,
} from "../../types/substrate";
import {
  measureSubstrateOperation,
  measureSubstrateOperationAsync,
} from "../monitoring/performanceMonitor";

import {
  applyDotGain,
  calculateTAC,
  checkInkLimits,
  calculateInkUsage,
  applyAbsorptionCoefficients,
  getOptimizedCMYK,
} from "./colorTransforms";
import {
  analyzeGamut,
  isColorInSubstrateGamut,
  calculateGamutDistance,
  findNearestInGamutColor,
  generateGamutVisualization,
  compareSubstrateGamuts,
  clearGamutBoundaryCache,
  getGamutCacheStats,
} from "./gamutMapping";
import {
  analyzeSubstrateAppearance,
  predictSubstrateColorAppearance,
  compareSubstrateAppearance,
  generateAppearanceReport,
  getDefaultViewingConditions,
  adjustViewingConditionsForSubstrate,
} from "./colorAppearance";
import { LayerStackManager, LayerUtils } from "./layerStack";
import { SpotColorManager } from "./spotColorManager";

export interface SubstrateEngineOptions {
  enableCaching?: boolean;
  defaultDotGain?: number;
  defaultGCR?: number;
  defaultUCR?: number;
}

export class SubstrateEngine {
  protected colorEngine: ColorEngine;
  private cache: Map<
    string,
    PrintSimulationResult | MultiLayerSimulationResult
  > = new Map();
  private options: SubstrateEngineOptions;
  private spotColorManager: SpotColorManager;

  constructor(colorEngine: ColorEngine, options: SubstrateEngineOptions = {}) {
    this.colorEngine = colorEngine;
    this.spotColorManager = new SpotColorManager();
    this.options = {
      enableCaching: options.enableCaching ?? true,
      defaultDotGain: options.defaultDotGain ?? 0.15,
      defaultGCR: options.defaultGCR ?? 0.5,
      defaultUCR: options.defaultUCR ?? 0.3,
    };
  }

  /**
   * Parse a color string and return color result
   * Public wrapper for colorEngine.parseColor
   */
  parseColor(input: string) {
    return this.colorEngine.parseColor(input);
  }

  /**
   * Simulate color printing on a specific substrate
   */
  simulateColor(
    inputColor: string,
    substrate: SubstrateProfile,
    options: {
      applyDotGain?: boolean;
      applyGCR?: boolean;
      applyUCR?: boolean;
      customGamma?: number;
    } = {},
  ): PrintSimulationResult {
    return measureSubstrateOperation("simulateColor", substrate.id, () => {
      const cacheKey = this.getCacheKey(inputColor, substrate, options);

      if (this.options.enableCaching && this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey)!;
      }

      // Parse input color using existing colorEngine
      const colorResult = this.colorEngine.parseColor(inputColor);
      if (!colorResult) {
        throw new Error(`Unable to parse color: ${inputColor}`);
      }

      const originalCMYK = colorResult.cmyk;

      // Use optimized CMYK transformation that applies all substrate-specific processing
      const simulatedCMYK = getOptimizedCMYK(originalCMYK, substrate, {
        applyAbsorption: true,
        applyColorTransform: true,
        applyGCR: options.applyGCR,
        applyUCR: options.applyUCR,
        applyDotGain: options.applyDotGain !== false,
        dotGainFactor: this.options.defaultDotGain,
        gcrAmount: this.options.defaultGCR,
        ucrAmount: this.options.defaultUCR,
        customGamma: options.customGamma,
      });

      // Calculate simulation result
      const result = this.buildSimulationResult(
        inputColor,
        colorResult,
        simulatedCMYK,
        substrate,
      );

      if (this.options.enableCaching) {
        this.cache.set(cacheKey, result);
      }

      return result;
    });
  }

  /**
   * Calculate ink usage for a given CMYK color
   */
  calculateInkUsage(
    cmyk: CMYK,
    area: number = 1000,
    inkDensity: number = 0.1,
  ): ReturnType<typeof calculateInkUsage> {
    return calculateInkUsage(cmyk, area, inkDensity);
  }

  /**
   * Check if ink limits are exceeded for a substrate
   */
  checkInkLimits(
    cmyk: CMYK,
    substrate: SubstrateProfile,
  ): ReturnType<typeof checkInkLimits> & {
    tac: { exceeded: boolean; percentage: number; limit: number };
  } {
    const individualLimits = checkInkLimits(cmyk, substrate.inkLimits);
    const tac = calculateTAC(cmyk);

    return {
      ...individualLimits,
      tac: {
        exceeded: tac > substrate.tacLimit,
        percentage: tac,
        limit: substrate.tacLimit,
      },
    };
  }

  /**
   * Apply dot gain to CMYK values
   */
  applyDotGain(cmyk: CMYK, factor?: number): CMYK {
    return applyDotGain(cmyk, factor ?? this.options.defaultDotGain);
  }

  /**
   * Get optimized CMYK values with all substrate-specific transformations applied
   */
  getOptimizedCMYK(
    cmyk: CMYK,
    substrate: SubstrateProfile,
    options: {
      applyAbsorption?: boolean;
      applyColorTransform?: boolean;
      applyGCR?: boolean;
      applyUCR?: boolean;
      applyDotGain?: boolean;
      dotGainFactor?: number;
      gcrAmount?: number;
      ucrAmount?: number;
      customGamma?: number;
    } = {},
  ): CMYK {
    return getOptimizedCMYK(cmyk, substrate, {
      applyAbsorption: options.applyAbsorption ?? true,
      applyColorTransform: options.applyColorTransform ?? true,
      applyGCR: options.applyGCR,
      applyUCR: options.applyUCR,
      applyDotGain: options.applyDotGain ?? true,
      dotGainFactor: options.dotGainFactor ?? this.options.defaultDotGain,
      gcrAmount: options.gcrAmount ?? this.options.defaultGCR,
      ucrAmount: options.ucrAmount ?? this.options.defaultUCR,
      customGamma: options.customGamma,
    });
  }

  /**
   * Apply absorption coefficients to simulate ink soaking into substrate
   */
  applyAbsorptionCoefficients(cmyk: CMYK, substrate: SubstrateProfile): CMYK {
    return applyAbsorptionCoefficients(cmyk, substrate.absorptionCoefficients);
  }

  /**
   * Calculate DeltaE between original and simulated colors
   */
  calculateDeltaE(
    originalColor: string,
    simulatedColor: string,
    method: "76" | "94" | "2000" = "2000",
  ): number | null {
    return this.colorEngine.calculateDeltaE(
      originalColor,
      simulatedColor,
      method,
    );
  }

  /**
   * Get substrate-specific color adjustments and suggestions
   */
  getColorAdjustments(
    cmyk: CMYK,
    substrate: SubstrateProfile,
  ): ColorAdjustment[] {
    const adjustments: ColorAdjustment[] = [];
    const limits = this.checkInkLimits(cmyk, substrate);

    // Check individual channel limits
    ["cyan", "magenta", "yellow", "black"].forEach((channel) => {
      const limit = limits[channel as keyof typeof limits];
      if (limit.exceeded) {
        const reduction = limit.percentage - limit.limit;
        adjustments.push({
          type: "ink-reduction",
          target: channel as "cyan" | "magenta" | "yellow" | "black",
          currentValue: limit.percentage,
          recommendedValue: limit.limit,
          expectedImprovement: {
            tacReduction: reduction,
          },
          priority:
            reduction > 20 ? "critical" : reduction > 10 ? "high" : "medium",
          autoApplicable: true,
          description: `Reduce ${channel} ink from ${limit.percentage.toFixed(1)}% to ${limit.limit}% to stay within substrate limits`,
        });
      }
    });

    // Check TAC limit
    if (limits.tac.exceeded) {
      const tacReduction = limits.tac.percentage - limits.tac.limit;
      adjustments.push({
        type: "ink-reduction",
        target: "tac",
        currentValue: limits.tac.percentage,
        recommendedValue: limits.tac.limit,
        expectedImprovement: {
          tacReduction,
        },
        priority:
          tacReduction > 50
            ? "critical"
            : tacReduction > 25
              ? "high"
              : "medium",
        autoApplicable: true,
        description: `Reduce total ink coverage from ${limits.tac.percentage.toFixed(1)}% to ${limits.tac.limit}% to prevent printing issues`,
      });
    }

    // Suggest substrate compensation for low brightness
    if (substrate.brightness < 70) {
      adjustments.push({
        type: "substrate-compensation",
        target: "brightness",
        currentValue: substrate.brightness,
        recommendedValue: Math.min(100, substrate.brightness + 10),
        expectedImprovement: {
          qualityImprovement: 5,
        },
        priority: "medium",
        autoApplicable: false,
        description:
          "Consider increasing ink density or using a brighter substrate for better color reproduction",
      });
    }

    return adjustments;
  }

  /**
   * Get print warnings for a color on a substrate
   */
  getPrintWarnings(cmyk: CMYK, substrate: SubstrateProfile): PrintWarning[] {
    const warnings: PrintWarning[] = [];
    const limits = this.checkInkLimits(cmyk, substrate);
    const timestamp = new Date().toISOString();

    // Individual channel warnings
    ["cyan", "magenta", "yellow", "black"].forEach((channel) => {
      const limit = limits[channel as keyof typeof limits];
      if (limit.exceeded) {
        warnings.push({
          id: `ink-limit-${channel}-${Date.now()}`,
          severity: limit.percentage > limit.limit * 1.2 ? "error" : "warning",
          category: "ink-limit",
          message: `${channel.charAt(0).toUpperCase() + channel.slice(1)} ink limit exceeded: ${limit.percentage.toFixed(1)}% > ${limit.limit}%`,
          details: `The ${channel} channel exceeds the substrate's maximum recommended ink coverage`,
          suggestions: [
            `Reduce ${channel} ink to ${limit.limit}% or lower`,
            "Consider using a different substrate with higher ink limits",
            "Apply under color removal to reduce overall ink usage",
          ],
          actionable: true,
          timestamp,
        });
      }
    });

    // TAC warnings
    if (limits.tac.exceeded) {
      warnings.push({
        id: `tac-limit-${Date.now()}`,
        severity:
          limits.tac.percentage > substrate.tacLimit * 1.1
            ? "error"
            : "warning",
        category: "tac-limit",
        message: `Total Area Coverage exceeded: ${limits.tac.percentage.toFixed(1)}% > ${substrate.tacLimit}%`,
        details:
          "Total ink coverage exceeds the substrate's maximum recommended limit",
        suggestions: [
          `Reduce total ink coverage to ${substrate.tacLimit}% or lower`,
          "Apply gray component replacement (GCR)",
          "Consider using a substrate with higher TAC limits",
        ],
        actionable: true,
        timestamp,
      });
    }

    // Quality warnings for low brightness substrates
    if (substrate.brightness < 60) {
      warnings.push({
        id: `brightness-${Date.now()}`,
        severity: "warning",
        category: "quality",
        message: `Low substrate brightness (${substrate.brightness}%) may affect color accuracy`,
        details: "Dark substrates can cause color shifts and reduced vibrancy",
        suggestions: [
          "Consider using a brighter substrate",
          "Increase ink density for better coverage",
          "Test print on actual substrate before production",
        ],
        actionable: false,
        timestamp,
      });
    }

    return warnings;
  }

  /**
   * Analyze gamut for a color on a substrate
   */
  analyzeGamut(
    color: string,
    substrate: SubstrateProfile,
    options?: {
      method?: "clip" | "compression" | "adaptive";
      preserveHue?: boolean;
      compressionStrength?: number;
    },
  ) {
    const safeOptions = {
      method: (options?.method ?? "clip") as
        | "clip"
        | "compression"
        | "adaptive",
      preserveHue: options?.preserveHue,
      compressionStrength: options?.compressionStrength,
    };
    return analyzeGamut(color, substrate, safeOptions);
  }

  /**
   * Check if color is within substrate gamut
   */
  isColorInSubstrateGamut(color: string, substrate: SubstrateProfile): boolean {
    return isColorInSubstrateGamut(color, substrate);
  }

  /**
   * Calculate distance from color to substrate gamut boundary
   */
  calculateGamutDistance(color: string, substrate: SubstrateProfile): number {
    return calculateGamutDistance(color, substrate);
  }

  /**
   * Find nearest in-gamut color for out-of-gamut colors
   */
  findNearestInGamutColor(
    color: string,
    substrate: SubstrateProfile,
    options?: {
      method?: "clip" | "compression" | "adaptive";
      preserveHue?: boolean;
      compressionStrength?: number;
    },
  ) {
    const safeOptions = {
      method:
        options?.method ?? ("clip" as "clip" | "compression" | "adaptive"),
      preserveHue: options?.preserveHue,
      compressionStrength: options?.compressionStrength,
    };
    return findNearestInGamutColor(color, substrate, safeOptions);
  }

  /**
   * Generate gamut visualization data
   */
  generateGamutVisualization(substrate: SubstrateProfile, resolution?: number) {
    return generateGamutVisualization(substrate, resolution);
  }

  /**
   * Compare gamuts between two substrates
   */
  compareSubstrateGamuts(
    substrate1: SubstrateProfile,
    substrate2: SubstrateProfile,
  ) {
    return compareSubstrateGamuts(substrate1, substrate2);
  }

  /**
   * Analyze color appearance changes due to substrate
   */
  analyzeSubstrateAppearance(
    color: string,
    substrate: SubstrateProfile,
    baseViewingConditions?: any,
  ) {
    return analyzeSubstrateAppearance(color, substrate, baseViewingConditions);
  }

  /**
   * Predict color appearance on substrate
   */
  predictSubstrateColorAppearance(
    color: string,
    substrate: SubstrateProfile,
    options?: {
      viewingConditions?: any;
      includeAdaptation?: boolean;
    },
  ) {
    return predictSubstrateColorAppearance(color, substrate, options);
  }

  /**
   * Compare appearance between two substrates
   */
  compareSubstrateAppearance(
    color: string,
    substrate1: SubstrateProfile,
    substrate2: SubstrateProfile,
    baseViewingConditions?: any,
  ) {
    return compareSubstrateAppearance(
      color,
      substrate1,
      substrate2,
      baseViewingConditions,
    );
  }

  /**
   * Generate appearance report for a color on multiple substrates
   */
  generateAppearanceReport(
    color: string,
    substrates: SubstrateProfile[],
    baseViewingConditions?: any,
  ) {
    return generateAppearanceReport(color, substrates, baseViewingConditions);
  }

  /**
   * Get default viewing conditions for print simulation
   */
  getDefaultViewingConditions(illuminant?: "D50" | "D65") {
    return getDefaultViewingConditions(illuminant);
  }

  /**
   * Adjust viewing conditions based on substrate properties
   */
  adjustViewingConditionsForSubstrate(
    baseConditions: any,
    substrate: SubstrateProfile,
  ) {
    return adjustViewingConditionsForSubstrate(baseConditions, substrate);
  }

  /**
   * Clear all caches (including gamut boundary cache)
   */
  clearAllCaches(): void {
    this.cache.clear();
    clearGamutBoundaryCache();
  }

  /**
   * Get comprehensive cache statistics
   */
  getAllCacheStats(): {
    substrateEngine: { size: number; maxSize: number };
    gamutMapping: { size: number; maxSize: number };
  } {
    return {
      substrateEngine: this.getCacheStats(),
      gamutMapping: getGamutCacheStats(),
    };
  }

  /**
   * Clear the simulation cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: 1000, // Could be made configurable
    };
  }

  private buildSimulationResult(
    inputColor: string,
    originalResult: any,
    simulatedCMYK: CMYK,
    substrate: SubstrateProfile,
  ): PrintSimulationResult {
    // Convert simulated CMYK back to hex for comparison
    const simulatedHex = this.colorEngine.cmykToHex(simulatedCMYK);
    const simulatedColor = this.colorEngine.parseColor(simulatedHex);

    if (!simulatedColor) {
      throw new Error("Failed to convert simulated CMYK back to color");
    }

    // Calculate color differences
    const deltaE76 = this.calculateDeltaE(inputColor, simulatedHex, "76") || 0;
    const deltaE94 = this.calculateDeltaE(inputColor, simulatedHex, "94") || 0;
    const deltaE2000 =
      this.calculateDeltaE(inputColor, simulatedHex, "2000") || 0;

    // Build print metrics
    const metrics = this.buildPrintMetrics(simulatedCMYK, substrate);

    // Get adjustments and warnings
    const adjustments = this.getColorAdjustments(simulatedCMYK, substrate);
    this.getPrintWarnings(simulatedCMYK, substrate);

    return {
      originalColor: {
        hex: originalResult.hex,
        rgb: originalResult.rgb,
        cmyk: originalResult.cmyk,
      },
      simulatedColor: {
        hex: simulatedColor.hex,
        rgb: simulatedColor.rgb,
        cmyk: this.enhanceCMYKWithLimits(simulatedCMYK, substrate),
      },
      substrate,
      colorDifference: {
        deltaE76,
        deltaE94,
        deltaE2000,
        perceptible: deltaE2000 > 2.0,
      },
      metrics,
      suggestions: adjustments,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Build print metrics for a given CMYK color and substrate
   */
  buildPrintMetrics(cmyk: CMYK, substrate: SubstrateProfile): PrintMetrics {
    return this.buildPrintMetricsInternal(cmyk, substrate);
  }

  /**
   * Enhance CMYK values with limit status information
   */
  enhanceCMYKWithLimits(cmyk: CMYK, substrate: SubstrateProfile): CMYKValues {
    return this.enhanceCMYKWithLimitsInternal(cmyk, substrate);
  }

  private buildPrintMetricsInternal(
    cmyk: CMYK,
    substrate: SubstrateProfile,
  ): PrintMetrics {
    const tac = calculateTAC(cmyk);
    const maxChannel = Math.max(cmyk.c, cmyk.m, cmyk.y, cmyk.k);
    const inkUsage = calculateInkUsage(cmyk);

    // Calculate quality indicators based on substrate properties and ink usage
    const quality = {
      sharpness: this.calculateSharpness(cmyk, substrate),
      colorAccuracy: this.calculateColorAccuracy(cmyk, substrate),
      consistency: this.calculateConsistency(cmyk, substrate),
    };

    return {
      tac,
      maxChannel,
      inkUsage: {
        percentage: {
          cyan: cmyk.c,
          magenta: cmyk.m,
          yellow: cmyk.y,
          black: cmyk.k,
        },
        milliliters: inkUsage,
        total: inkUsage.total,
      },
      quality,
    };
  }

  private enhanceCMYKWithLimitsInternal(
    cmyk: CMYK,
    substrate: SubstrateProfile,
  ): CMYKValues {
    const limits = this.checkInkLimits(cmyk, substrate);
    const tac = calculateTAC(cmyk);

    return {
      ...cmyk,
      inkLimitStatus: {
        cyan: {
          percentage: limits.cyan.percentage,
          limit: limits.cyan.limit,
          exceeded: limits.cyan.exceeded,
          warningLevel: limits.cyan.exceeded
            ? limits.cyan.percentage > limits.cyan.limit * 1.2
              ? "critical"
              : "warning"
            : "none",
        },
        magenta: {
          percentage: limits.magenta.percentage,
          limit: limits.magenta.limit,
          exceeded: limits.magenta.exceeded,
          warningLevel: limits.magenta.exceeded
            ? limits.magenta.percentage > limits.magenta.limit * 1.2
              ? "critical"
              : "warning"
            : "none",
        },
        yellow: {
          percentage: limits.yellow.percentage,
          limit: limits.yellow.limit,
          exceeded: limits.yellow.exceeded,
          warningLevel: limits.yellow.exceeded
            ? limits.yellow.percentage > limits.yellow.limit * 1.2
              ? "critical"
              : "warning"
            : "none",
        },
        black: {
          percentage: limits.black.percentage,
          limit: limits.black.limit,
          exceeded: limits.black.exceeded,
          warningLevel: limits.black.exceeded
            ? limits.black.percentage > limits.black.limit * 1.2
              ? "critical"
              : "warning"
            : "none",
        },
      },
      tac,
      tacExceeded: limits.tac.exceeded,
    };
  }

  private calculateSharpness(_cmyk: CMYK, substrate: SubstrateProfile): number {
    // Sharpness is affected by dot gain and substrate absorption
    const dotGainPenalty = Math.max(
      0,
      ((this.options.defaultDotGain ?? 0.15) - 0.1) * 100,
    );
    const absorptionPenalty =
      Object.values(substrate.absorptionCoefficients).reduce(
        (sum, coeff) => sum + coeff,
        0,
      ) * 20;

    return Math.max(0, Math.min(100, 100 - dotGainPenalty - absorptionPenalty));
  }

  private calculateColorAccuracy(
    cmyk: CMYK,
    substrate: SubstrateProfile,
  ): number {
    // Color accuracy is affected by substrate brightness and color shifts
    const brightnessBonus = substrate.brightness * 0.5;
    const colorTransformBonus = substrate.colorTransform ? 10 : 0;
    const tacPenalty =
      Math.max(0, calculateTAC(cmyk) - substrate.tacLimit * 0.8) * 0.5;

    return Math.max(
      0,
      Math.min(100, brightnessBonus + colorTransformBonus - tacPenalty),
    );
  }

  private calculateConsistency(
    _cmyk: CMYK,
    substrate: SubstrateProfile,
  ): number {
    // Consistency is affected by substrate uniformity and ink limits
    const baseScore = 80;
    const glossinessBonus = substrate.glossiness * 0.1;
    const categoryBonus =
      substrate.category === "paper"
        ? 5
        : substrate.category === "metallic"
          ? 0
          : 2;

    return Math.max(
      0,
      Math.min(100, baseScore + glossinessBonus + categoryBonus),
    );
  }

  /**
   * Simulate multi-layer printing on a substrate
   */
  simulateLayerStack(
    layerStack: LayerStack,
    options: {
      applyDotGain?: boolean;
      applyGCR?: boolean;
      applyUCR?: boolean;
      customGamma?: number;
    } = {},
  ): MultiLayerSimulationResult {
    const cacheKey = this.getLayerStackCacheKey(layerStack, options);

    if (this.options.enableCaching && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as MultiLayerSimulationResult;
    }

    // Validate layer stack
    const validation = this.validateLayerStack(layerStack);
    if (!validation.valid) {
      throw new Error(`Invalid layer stack: ${validation.errors.join(", ")}`);
    }

    // Get visible layers sorted by zIndex (bottom to top)
    const visibleLayers = layerStack.layers
      .filter((layer) => layer.visible)
      .sort((a, b) => a.zIndex - b.zIndex);

    if (visibleLayers.length === 0) {
      throw new Error("No visible layers in layer stack");
    }

    // Initialize with substrate base color (as CMYK)
    let accumulatedCMYK: CMYK = this.convertSubstrateBaseToCMYK(
      layerStack.substrate,
    );
    const layerResults: LayerSimulationResult[] = [];

    // Process each layer
    for (const layer of visibleLayers) {
      let layerCMYK: CMYK;
      let spotConversionResult: SpotColorConversionResult | undefined;

      // Check if layer uses spot color
      if (
        this.isPrintLayerWithSpot(layer) &&
        layer.useSpotColor &&
        layer.spotColor
      ) {
        // Convert spot color to CMYK for this substrate
        spotConversionResult = this.spotColorManager.convertSpotToProcess(
          layer.spotColor,
          layerStack.substrate,
          {
            substrate: layerStack.substrate,
            method: "approximation",
            qualityThreshold: 70,
            allowFallback: true,
          },
        );

        layerCMYK = spotConversionResult.cmyk;

        // Apply spot opacity override if specified
        if (layer.spotSettings?.spotOpacity !== undefined) {
          layerCMYK = {
            c: layerCMYK.c * layer.spotSettings.spotOpacity,
            m: layerCMYK.m * layer.spotSettings.spotOpacity,
            y: layerCMYK.y * layer.spotSettings.spotOpacity,
            k: layerCMYK.k * layer.spotSettings.spotOpacity,
          };
        }
      } else {
        // Use regular CMYK values
        layerCMYK = layer.cmyk;
      }

      // Apply substrate effects to individual layer
      const processedLayerCMYK = this.getOptimizedCMYK(
        layerCMYK,
        layerStack.substrate,
        {
          applyAbsorption: true,
          applyColorTransform: true,
          applyGCR: options.applyGCR,
          applyUCR: options.applyUCR,
          applyDotGain: options.applyDotGain !== false,
          dotGainFactor: this.options.defaultDotGain,
          gcrAmount: this.options.defaultGCR,
          ucrAmount: this.options.defaultUCR,
          customGamma: options.customGamma,
        },
      );

      // Blend with accumulated result
      accumulatedCMYK = LayerUtils.blendCMYK(
        accumulatedCMYK,
        processedLayerCMYK,
        layer.blendMode,
        layer.opacity,
      );

      // Create layer result
      const layerResult = this.createLayerSimulationResult(
        layer,
        processedLayerCMYK,
        layerStack.substrate,
        spotConversionResult,
      );
      layerResults.push(layerResult);
    }

    // Build final multi-layer result
    const result = this.buildMultiLayerSimulationResult(
      layerStack,
      layerResults,
      accumulatedCMYK,
    );

    if (this.options.enableCaching) {
      this.cache.set(cacheKey, result);
    }

    return result;
  }

  /**
   * Export layer stack as flattened or separated layers
   */
  exportLayerStack(
    layerStack: LayerStack,
    format: "flattened" | "separated" | "both" = "flattened",
  ): {
    flattened?: { cmyk: CMYK; hex: string };
    separated?: Array<{ layer: PrintLayer; cmyk: CMYK; hex: string }>;
  } {
    const result: any = {};

    if (format === "flattened" || format === "both") {
      const simulation = this.simulateLayerStack(layerStack);
      result.flattened = {
        cmyk: simulation.flattenedResult.cmyk,
        hex: simulation.flattenedResult.hex,
      };
    }

    if (format === "separated" || format === "both") {
      const visibleLayers = layerStack.layers
        .filter((layer) => layer.visible)
        .sort((a, b) => a.zIndex - b.zIndex);

      result.separated = visibleLayers.map((layer) => {
        const processedCMYK = this.getOptimizedCMYK(
          layer.cmyk,
          layerStack.substrate,
          {
            applyAbsorption: true,
            applyColorTransform: true,
            applyDotGain: true,
            dotGainFactor: this.options.defaultDotGain,
          },
        );

        const hex = this.colorEngine.cmykToHex(processedCMYK);

        return {
          layer,
          cmyk: processedCMYK,
          hex,
        };
      });
    }

    return result;
  }

  private getCacheKey(
    color: string,
    substrate: SubstrateProfile,
    options: Record<string, any>,
  ): string {
    const optionsHash = JSON.stringify(options);
    return `${color}:${substrate.id}:${optionsHash}`;
  }

  private getLayerStackCacheKey(
    layerStack: LayerStack,
    options: Record<string, any>,
  ): string {
    // Create hash from visible layers and their properties
    const visibleLayersHash = layerStack.layers
      .filter((layer) => layer.visible)
      .sort((a, b) => a.zIndex - b.zIndex)
      .map(
        (layer) =>
          `${layer.id}:${layer.cmyk.c},${layer.cmyk.m},${layer.cmyk.y},${layer.cmyk.k}:${layer.opacity}:${layer.blendMode}`,
      )
      .join("|");

    const optionsHash = JSON.stringify(options);
    return `layerstack:${layerStack.substrate.id}:${visibleLayersHash}:${optionsHash}`;
  }

  private validateLayerStack(layerStack: LayerStack): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!layerStack.layers || layerStack.layers.length === 0) {
      errors.push("Layer stack must contain at least one layer");
    }

    const visibleLayers = layerStack.layers.filter((layer) => layer.visible);
    if (visibleLayers.length === 0) {
      errors.push("Layer stack must have at least one visible layer");
    }

    // Check for duplicate zIndex values
    const zIndexValues = layerStack.layers.map((layer) => layer.zIndex);
    const uniqueZIndexValues = new Set(zIndexValues);
    if (zIndexValues.length !== uniqueZIndexValues.size) {
      errors.push("Duplicate zIndex values detected in layer stack");
    }

    // Validate individual layers
    layerStack.layers.forEach((layer, index) => {
      if (layer.opacity < 0 || layer.opacity > 1) {
        errors.push(
          `Layer ${index} "${layer.name}" has invalid opacity: ${layer.opacity}`,
        );
      }

      if (
        layer.cmyk.c < 0 ||
        layer.cmyk.c > 100 ||
        layer.cmyk.m < 0 ||
        layer.cmyk.m > 100 ||
        layer.cmyk.y < 0 ||
        layer.cmyk.y > 100 ||
        layer.cmyk.k < 0 ||
        layer.cmyk.k > 100
      ) {
        errors.push(`Layer ${index} "${layer.name}" has invalid CMYK values`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  private convertSubstrateBaseToCMYK(substrate: SubstrateProfile): CMYK {
    // Convert substrate base color to CMYK
    const baseColor = this.colorEngine.parseColor(substrate.baseColor.hex);
    if (!baseColor) {
      // Default to white substrate if parsing fails
      return { c: 0, m: 0, y: 0, k: 0 };
    }
    return baseColor.cmyk;
  }

  /**
   * Type guard to check if a layer has spot color properties
   */
  private isPrintLayerWithSpot(layer: PrintLayer): layer is PrintLayerWithSpot {
    return "spotColor" in layer && "useSpotColor" in layer;
  }

  private createLayerSimulationResult(
    layer: PrintLayer,
    processedCMYK: CMYK,
    substrate: SubstrateProfile,
    spotConversionResult?: SpotColorConversionResult,
  ): LayerSimulationResult {
    // Convert processed CMYK to hex for display
    const hex = this.colorEngine.cmykToHex(processedCMYK);
    const color = this.colorEngine.parseColor(hex);

    if (!color) {
      throw new Error("Failed to convert layer CMYK to color");
    }

    // Get layer-specific metrics and warnings
    const metrics = this.buildPrintMetrics(processedCMYK, substrate);
    const warnings = this.getPrintWarnings(processedCMYK, substrate);

    // Add spot color specific warnings if applicable
    if (spotConversionResult && spotConversionResult.quality < 70) {
      warnings.push({
        id: `spot-color-quality-${Date.now()}`,
        category: "quality",
        severity: "warning",
        message: `Low quality spot color conversion (${spotConversionResult.quality}%): ${spotConversionResult.notes}`,
        actionable: true,
        suggestions: [
          "Consider using a different substrate or spot color for better results",
        ],
        timestamp: new Date().toISOString(),
      });
    }

    return {
      layer,
      simulatedColor: {
        hex: color.hex,
        rgb: color.rgb,
        cmyk: this.enhanceCMYKWithLimits(processedCMYK, substrate),
      },
      metrics,
      warnings,
      affectsOutput: layer.visible && layer.opacity > 0,
    };
  }

  private buildMultiLayerSimulationResult(
    layerStack: LayerStack,
    layerResults: LayerSimulationResult[],
    flattenedCMYK: CMYK,
  ): MultiLayerSimulationResult {
    // Convert flattened CMYK to final color
    const flattenedHex = this.colorEngine.cmykToHex(flattenedCMYK);
    const flattenedColor = this.colorEngine.parseColor(flattenedHex);

    if (!flattenedColor) {
      throw new Error("Failed to convert flattened CMYK to color");
    }

    // Calculate composite metrics
    const totalInkUsage = layerResults.reduce(
      (sum, result) => sum + result.metrics.inkUsage.total,
      0,
    );
    const layerTACs = layerResults.map((result) => result.metrics.tac);
    const maxLayerTAC = Math.max(...layerTACs);
    const averageLayerTAC =
      layerTACs.reduce((sum, tac) => sum + tac, 0) / layerTACs.length;

    // Create base simulation result (for compatibility)
    const baseResult: PrintSimulationResult = {
      originalColor: {
        hex: flattenedColor.hex,
        rgb: flattenedColor.rgb,
        cmyk: flattenedCMYK,
      },
      simulatedColor: {
        hex: flattenedColor.hex,
        rgb: flattenedColor.rgb,
        cmyk: this.enhanceCMYKWithLimits(flattenedCMYK, layerStack.substrate),
      },
      substrate: layerStack.substrate,
      colorDifference: {
        deltaE76: 0, // No original color to compare with for multi-layer
        deltaE94: 0,
        deltaE2000: 0,
        perceptible: false,
      },
      metrics: this.buildPrintMetrics(flattenedCMYK, layerStack.substrate),
      suggestions: this.getColorAdjustments(
        flattenedCMYK,
        layerStack.substrate,
      ),
      timestamp: new Date().toISOString(),
    };

    return {
      ...baseResult,
      layerStack,
      layerResults,
      flattenedResult: {
        hex: flattenedColor.hex,
        rgb: flattenedColor.rgb,
        cmyk: this.enhanceCMYKWithLimits(flattenedCMYK, layerStack.substrate),
      },
      compositeMetrics: {
        totalInkUsage,
        layerCount: layerStack.layers.length,
        maxLayerTAC,
        averageLayerTAC,
      },
    };
  }

  /**
   * Get the spot color manager instance
   */
  getSpotColorManager(): SpotColorManager {
    return this.spotColorManager;
  }

  /**
   * Load a spot color library
   */
  async loadSpotColorLibrary(libraryData: any): Promise<void> {
    await this.spotColorManager.loadLibrary(libraryData);
  }

  /**
   * Load a spot color library from URL
   */
  async loadSpotColorLibraryFromUrl(url: string): Promise<void> {
    await this.spotColorManager.loadLibraryFromUrl(url);
  }

  /**
   * Search for spot colors
   */
  searchSpotColors(
    query: string,
    options?: {
      system?: any;
      limit?: number;
      includeAliases?: boolean;
    },
  ): any[] {
    return this.spotColorManager.searchColors(query, options);
  }

  /**
   * Get spot color by ID
   */
  getSpotColorById(colorId: string): any | undefined {
    return this.spotColorManager.getColorById(colorId);
  }

  /**
   * Get spot color by system and code
   */
  getSpotColorByCode(system: any, code: string): any | undefined {
    return this.spotColorManager.getColorByCode(system, code);
  }

  /**
   * Convert spot color to CMYK
   */
  convertSpotColorToProcess(
    spotColor: any,
    substrate: SubstrateProfile,
    options?: any,
  ): any {
    return this.spotColorManager.convertSpotToProcess(
      spotColor,
      substrate,
      options,
    );
  }

  /**
   * Get recommended spot colors for a substrate
   */
  getRecommendedSpotColors(substrate: SubstrateProfile, limit?: number): any[] {
    return this.spotColorManager.getRecommendedColors(substrate, limit);
  }

  /**
   * Get spot color library statistics
   */
  getSpotColorStatistics(): any {
    return this.spotColorManager.getStatistics();
  }
}
